import { ComponentPropsWithoutRef, useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import Button from '@/design_system/Button';
import Checkbox from '@/design_system/Checkbox';
import ConfirmationDialog from '@/design_system/Dialog/ConfirmationDialog';
import { DangerMenuItem } from '@/design_system/Menu/Menu';
import Stack from '@/design_system/Stack';
import TextArea from '@/design_system/TextArea';
import { useShowToast } from '@/design_system/Toast';
import InlineText from '@/design_system/Typography/InlineText';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconArchive from '@/icons/Archive.svg';
import { RequestWithRelations, useArchiveRequest } from '@/models/request';
import useViewPort from '@/utils/useViewport';

export const ArchiveRequestDialog = ({
  request,
  onArchive,
  onArchiveError,
  showArchivalConfirmation,
  setShowArchivalConfirmation,
}: {
  request: RequestWithRelations;
  onArchive?: () => void;
  onArchiveError?: (reason: unknown) => void;
  showArchivalConfirmation: boolean;
  setShowArchivalConfirmation: (showArchivalConfirmation: boolean) => void;
}) => {
  const { t } = useLingui();

  const showToast = useShowToast();

  const { mutateAsync: archiveRequest } = useArchiveRequest();

  const [exportToZendesk, setExportToZendesk] = useState(false);
  const [archivalReason, setArchivalReason] = useState<string>();

  const hasZendeskIntegration = !!request.organization.config.integrations?.zendesk;

  return (
    <ConfirmationDialog
      title={<Trans id="requests.edit.archival-modal.title">Request archival</Trans>}
      confirm={<Trans id="requests.edit.archival-modal.confirm">Archive request</Trans>}
      onConfirm={() => {
        archiveRequest({
          id: request.id,
          query: {
            ...(exportToZendesk ? { exportedTo: 'zendesk' } : undefined),
            reason: archivalReason,
          },
        })
          .then(() => {
            setShowArchivalConfirmation(false);
            showToast({
              type: 'success',
              text: t({
                id: 'requests.edit.archival-modal.success',
                message: 'The request has been successfully archived',
              }),
            });
          })
          .then(onArchive)
          .catch(onArchiveError);
      }}
      showDialog={showArchivalConfirmation}
      setShowDialog={setShowArchivalConfirmation}
    >
      <Stack gap="1rem">
        <Paragraph>
          <Trans id="requests.edit.archival-modal.content">
            Are you sure you want to archive this request? This action cannot be undone.
          </Trans>
        </Paragraph>

        <TextArea
          ariaLabel={t({
            id: 'requests.edit.archival-modal.reason.label',
            message: 'Specify the reason...',
          })}
          placeholder={t({
            id: 'requests.edit.archival-modal.details.label',
            message: 'Specify the reason...',
          })}
          value={archivalReason}
          onChange={(evt) => setArchivalReason(evt.target.value)}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          rows={3}
        />

        {hasZendeskIntegration && (
          <Checkbox isSelected={exportToZendesk} onChange={setExportToZendesk}>
            <p>
              <Trans id="requests.edit.archival-modal.content.zendesk">
                Transfer this request to Zendesk
              </Trans>
            </p>
          </Checkbox>
        )}
      </Stack>
    </ConfirmationDialog>
  );
};

export const ArchiveRequestButton = ({
  request,
  onArchive,
  onArchiveError,
  iconOnly,
  size,
}: {
  request: RequestWithRelations;
  onArchive?: () => void;
  onArchiveError?: (reason: unknown) => void;
  iconOnly?: boolean;
  size?: ComponentPropsWithoutRef<typeof Button>['size'];
}) => {
  const { isMobile } = useViewPort();
  const { t } = useLingui();

  const [showArchivalConfirmation, setShowArchivalConfirmation] = useState(false);

  return (
    <>
      <Button
        variant="secondary"
        size={size ?? 'medium'}
        iconOnly={iconOnly}
        onPress={() => setShowArchivalConfirmation(true)}
        disabled={showArchivalConfirmation}
        style={isMobile ? { flex: 1 } : undefined}
        ariaLabel={t({ id: 'requests.edit.actions.archive', message: 'Archive request' })}
        tooltip={
          iconOnly
            ? t({ id: 'requests.edit.actions.archive', message: 'Archive request' })
            : undefined
        }
      >
        <IconArchive />
        {!isMobile && !iconOnly && (
          <Trans id="requests.edit.actions.archive">Archive request</Trans>
        )}
      </Button>
      <ArchiveRequestDialog
        request={request}
        onArchive={onArchive}
        onArchiveError={onArchiveError}
        showArchivalConfirmation={showArchivalConfirmation}
        setShowArchivalConfirmation={setShowArchivalConfirmation}
      />
    </>
  );
};

export const ArchiveRequestMenuItem = () => {
  return (
    <DangerMenuItem id="archive">
      <Stack row alignItems="center" gap="0.5rem">
        <IconArchive style={{ fontSize: '1.25rem' }} />
        <InlineText>
          <Trans id="requests.edit.actions.archive">Archive request</Trans>
        </InlineText>
      </Stack>
    </DangerMenuItem>
  );
};
